import React from 'react';
import { STANDARD_SCENE_COLORS } from '../../models/sceneColorPalette';
import { PRODUCTION_WORK_STATUS_PRESENTATIONS } from '../../models/productionWorkStatus';
import { getBreakdownSidebarCategoryPresentation } from '../../utils/breakdownPresentation';
import { MOOD_LOOK_PRESETS, STOCK_SOURCES } from './landingContent';

/*
 * Hand-built pictures of the editor, drawn with the same strip colours,
 * category colours and DOOD codes the app uses. The scenes come from the
 * bundled demo project, "The Last Horizon", so what a visitor sees here is
 * what they find when they open the demo.
 *
 * Each picture is a single labelled image for assistive technology; the
 * text inside is decoration.
 */

const categoryStyle = (category: string): React.CSSProperties => {
  const color = getBreakdownSidebarCategoryPresentation(category).sidebarColor;
  return { '--lp-cat': color, '--lp-cat-soft': withAlpha(color, 0.32) } as React.CSSProperties;
};

const withAlpha = (hex: string, alpha: number): string => {
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

const Mark: React.FC<{ category: string; delay: number; children: React.ReactNode }> = ({ category, delay, children }) => (
  <mark className="lp-mark" style={{ ...categoryStyle(category), animationDelay: `${delay}ms` }}>{children}</mark>
);

type StripTone = keyof typeof STANDARD_SCENE_COLORS;

interface StripRow {
  scene: string;
  setting: 'INT' | 'EXT';
  location: string;
  time: string;
  tone: StripTone;
  pages: string;
  cast: string;
  oneLiner?: string;
  isNew?: boolean;
}

type BoardRow = StripRow | { dayBreak: string; total: string } | { banner: string };

const Strip: React.FC<{ row: StripRow; showOneLiner?: boolean }> = ({ row, showOneLiner }) => (
  <div
    className={`lp-strip${row.isNew ? ' is-new' : ''}`}
    style={{ '--lp-strip': STANDARD_SCENE_COLORS[row.tone] } as React.CSSProperties}
  >
    <span className="lp-strip-no">{row.scene}</span>
    <span className="lp-strip-slug">
      <span className="lp-strip-heading">{row.setting}. {row.location}</span>
      {showOneLiner && row.oneLiner && <span className="lp-strip-oneliner">{row.oneLiner}</span>}
    </span>
    <span className="lp-strip-time">{row.time}</span>
    <span className="lp-strip-cast">{row.cast}</span>
    <span className="lp-strip-pages">{row.pages}</span>
  </div>
);

const BoardRows: React.FC<{ rows: readonly BoardRow[]; showOneLiner?: boolean }> = ({ rows, showOneLiner }) => (
  <>
    {rows.map((row, index) => {
      if ('dayBreak' in row) {
        return (
          <div key={`break-${index}`} className="lp-daybreak">
            <span>{row.dayBreak}</span>
            <span>{row.total}</span>
          </div>
        );
      }
      if ('banner' in row) {
        return <div key={`banner-${index}`} className="lp-banner">{row.banner}</div>;
      }
      return <Strip key={row.scene} row={row} showOneLiner={showOneLiner} />;
    })}
  </>
);

const HERO_BOARD: readonly BoardRow[] = [
  { scene: '3', setting: 'INT', location: 'MESS HALL', time: 'MORNING', tone: 'morning', pages: '1 3/8', cast: '1, 2' },
  { scene: '6', setting: 'INT', location: 'AIRLOCK 7', time: 'DAY', tone: 'interiorDay', pages: '1 2/8', cast: '1, 2', isNew: true },
  { scene: '10', setting: 'INT', location: 'MISSION CONTROL', time: 'DAY', tone: 'interiorDay', pages: '1 1/8', cast: '3, 6, 7' },
  { dayBreak: 'End of Day 1', total: '3 6/8 pgs' },
  { scene: '7', setting: 'EXT', location: 'MARS SURFACE - SECTOR 4', time: 'DAY', tone: 'exteriorDay', pages: '4/8', cast: '1, 2' },
  { scene: '8', setting: 'EXT', location: 'MARS SURFACE - DUNES', time: 'DAY', tone: 'exteriorDay', pages: '2/8', cast: '—' },
];

const HERO_TAGS = [
  { category: 'CAST', item: 'Sarah Chen' },
  { category: 'CAST', item: 'Cole Martinez' },
  { category: 'COSTUMES', item: 'EVA suits' },
  { category: 'PROPS', item: 'Replacement valves' },
  { category: 'SOUND', item: 'Airlock hiss' },
] as const;

export const HeroVisual: React.FC = () => (
  <div
    className="lp-hero-visual"
    role="img"
    aria-label="A screenplay page for scene 6, INT. Airlock 7, with cast, costumes, props and sound highlighted, next to a stripboard where scene 6 has been placed on day 1."
  >
    <div className="lp-scriptpage" aria-hidden="true">
      <span className="lp-scriptpage-no">12.</span>
      <p className="lp-sp-slug"><span>6</span>INT. AIRLOCK 7 - DAY<span>6</span></p>
      <p className="lp-sp-action">
        <Mark category="CAST" delay={500}>Sarah</Mark> and <Mark category="CAST" delay={650}>Cole</Mark> pull
        on <Mark category="COSTUMES" delay={800}>environment suits</Mark> with methodical, practiced movements.
        Three <Mark category="PROPS" delay={950}>replacement valves</Mark> sit in a carrying case near the outer door.
      </p>
      <p className="lp-sp-character">COLE</p>
      <p className="lp-sp-paren">(checking his wrist computer)</p>
      <p className="lp-sp-dialogue">Six hours, forty minutes of O2. That gives us time for the repair.</p>
      <p className="lp-sp-action">
        Sarah hits the DEPRESSURIZE button. <Mark category="SOUND" delay={1100}>The airlock hisses.</Mark>
      </p>
    </div>

    <ul className="lp-tagstack" aria-hidden="true">
      {HERO_TAGS.map((tag, index) => (
        <li key={tag.item} className="lp-tag" style={{ ...categoryStyle(tag.category), animationDelay: `${1300 + index * 120}ms` }}>
          <span className="lp-tag-cat">{tag.category}</span>
          <span>{tag.item}</span>
        </li>
      ))}
    </ul>

    <div className="lp-miniboard" aria-hidden="true">
      <div className="lp-panel-bar">
        <span>Stripboard</span>
        <span className="lp-panel-meta">The Last Horizon</span>
      </div>
      <div className="lp-board-body">
        <BoardRows rows={HERO_BOARD} />
      </div>
    </div>
  </div>
);

const SHEET_CATEGORIES = [
  { category: 'CAST', items: ['1. Lt. Sarah Chen', '2. Engineer Cole Martinez'] },
  { category: 'COSTUMES', items: ['Environment suit — Sarah', 'Environment suit — Cole'] },
  { category: 'PROPS', items: ['Replacement valves', 'Carrying case', 'Wrist computer'] },
  { category: 'SOUND', items: ['Helmet seal click', 'Airlock hiss'] },
  { category: 'VFX', items: ['Martian surface through outer window'] },
  { category: 'EQUIPMENT', items: ['Oxygen tanks'] },
] as const;

export const BreakdownSheetVisual: React.FC = () => (
  <div
    className="lp-paper lp-sheet"
    role="img"
    aria-label="Breakdown sheet for scene 6, INT. Airlock 7, day, one and two eighths pages, listing cast, costumes, props, sound, VFX and equipment."
  >
    <div className="lp-sheet-head" aria-hidden="true">
      <div>
        <span className="lp-sheet-label">Scene</span>
        <strong>6</strong>
      </div>
      <div className="lp-sheet-slug">
        <span className="lp-sheet-label">Heading</span>
        <strong>INT. AIRLOCK 7 — DAY</strong>
      </div>
      <div>
        <span className="lp-sheet-label">Pages</span>
        <strong>1 2/8</strong>
      </div>
    </div>
    <p className="lp-sheet-synopsis" aria-hidden="true">
      Sarah and Cole suit up for the Sector 4 EVA, secretly planning a detour south.
    </p>
    <div className="lp-sheet-grid" aria-hidden="true">
      {SHEET_CATEGORIES.map(({ category, items }) => (
        <div key={category} className="lp-sheet-cell" style={categoryStyle(category)}>
          <span className="lp-sheet-cat">{category}</span>
          <ul>
            {items.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const SCHEDULE_BOARD: readonly BoardRow[] = [
  { banner: 'Stage 2 — Colony interiors' },
  { scene: '3', setting: 'INT', location: 'MESS HALL', time: 'MORNING', tone: 'morning', pages: '1 3/8', cast: '1, 2', oneLiner: 'Cole shows Sarah the strange signals over breakfast.' },
  { scene: '6', setting: 'INT', location: 'AIRLOCK 7', time: 'DAY', tone: 'interiorDay', pages: '1 2/8', cast: '1, 2', oneLiner: 'Sarah and Cole suit up for the Sector 4 EVA.' },
  { scene: '10', setting: 'INT', location: 'MISSION CONTROL', time: 'DAY', tone: 'interiorDay', pages: '1 1/8', cast: '3, 6, 7', oneLiner: 'Ravi reports the rover has gone off route.' },
  { dayBreak: 'End of Day 1 — Mon', total: '3 6/8 pgs · 3 scenes' },
  { banner: 'Company move — Desert unit' },
  { scene: '7', setting: 'EXT', location: 'MARS SURFACE - SECTOR 4', time: 'DAY', tone: 'exteriorDay', pages: '4/8', cast: '1, 2', oneLiner: 'The processor repair, racing the storm.' },
  { scene: '8', setting: 'EXT', location: 'MARS SURFACE - DUNES', time: 'DAY', tone: 'exteriorDay', pages: '2/8', cast: '—', oneLiner: 'Rover Alpha blasts across the dunes.' },
  { scene: '11', setting: 'EXT', location: 'CANYON RIM - VALLES MARINERIS', time: 'DUSK', tone: 'evening', pages: '3/8', cast: '1, 2', oneLiner: 'An impossible doorway in the canyon wall.' },
  { scene: '19', setting: 'EXT', location: 'MARS SURFACE - CANYON PATH', time: 'NIGHT', tone: 'exteriorNight', pages: '5/8', cast: '1, 2', oneLiner: 'The storm hits with full force.' },
  { dayBreak: 'End of Day 2 — Tue', total: '1 6/8 pgs · 4 scenes' },
];

export const StripboardVisual: React.FC = () => (
  <div
    className="lp-panel lp-schedule"
    role="img"
    aria-label="A stripboard with two shooting days. Strips are coloured by setting and time of day, separated by day breaks and banners for a stage block and a company move."
  >
    <div className="lp-panel-bar" aria-hidden="true">
      <span>Stripboard</span>
      <span className="lp-panel-tabs">
        <span className="is-active">Scene/Day</span>
        <span>Filters</span>
        <span>Conflicts</span>
        <span>Versions</span>
      </span>
    </div>
    <div className="lp-strip-head" aria-hidden="true">
      <span>Sc.</span>
      <span>Set / one-liner</span>
      <span>Time</span>
      <span>Cast</span>
      <span>Pages</span>
    </div>
    <div className="lp-board-body" aria-hidden="true">
      <BoardRows rows={SCHEDULE_BOARD} showOneLiner />
    </div>
  </div>
);

export const StripLegend: React.FC = () => (
  <ul className="lp-legend" aria-label="Strip colours">
    {([
      ['interiorDay', 'INT day'],
      ['exteriorDay', 'EXT day'],
      ['interiorNight', 'INT night'],
      ['exteriorNight', 'EXT night'],
      ['morning', 'Dawn / morning'],
      ['evening', 'Dusk / evening'],
    ] as const).map(([tone, label]) => (
      <li key={tone}>
        <span className="lp-legend-swatch" style={{ background: STANDARD_SCENE_COLORS[tone] }} aria-hidden="true" />
        {label}
      </li>
    ))}
  </ul>
);

const CALL_CAST = [
  ['1', 'Sarah Chen', 'SW', '06:15', '07:00', '07:45'],
  ['2', 'Cole Martinez', 'SW', '06:30', '07:15', '07:45'],
  ['3', 'Cmdr. Vance', 'SW', '08:00', '08:45', '09:30'],
  ['6', 'Ravi Patel', 'SWF', '08:15', '09:00', '09:30'],
] as const;

export const CallSheetVisual: React.FC = () => (
  <div
    className="lp-paper lp-callsheet"
    role="img"
    aria-label="Call sheet for day 1 of 18 with crew call, lunch and wrap times, the scene schedule, and cast pickup, makeup and on-set times."
  >
    <div className="lp-cs-top" aria-hidden="true">
      <div>
        <span className="lp-sheet-label">The Last Horizon</span>
        <strong className="lp-cs-title">Call Sheet</strong>
        <span className="lp-cs-day">Day 1 of 18 · Monday</span>
      </div>
      <dl className="lp-cs-times">
        <div><dt>Crew call</dt><dd>07:00</dd></div>
        <div><dt>Lunch</dt><dd>13:00</dd></div>
        <div><dt>Wrap</dt><dd>19:00</dd></div>
      </dl>
    </div>
    <div className="lp-cs-row" aria-hidden="true">
      <div><span className="lp-sheet-label">Base & catering</span>Stage 2, west lot</div>
      <div><span className="lp-sheet-label">Nearest hospital</span>Listed with parking</div>
      <div><span className="lp-sheet-label">Weather</span>Sunny · 24° / 13°</div>
    </div>
    <table className="lp-cs-table" aria-hidden="true">
      <thead>
        <tr><th>Sc.</th><th>Set</th><th>D/N</th><th>Pgs</th></tr>
      </thead>
      <tbody>
        <tr><td>3</td><td>INT. Mess Hall</td><td>M</td><td>1 3/8</td></tr>
        <tr><td>6</td><td>INT. Airlock 7</td><td>D</td><td>1 2/8</td></tr>
        <tr><td>10</td><td>INT. Mission Control</td><td>D</td><td>1 1/8</td></tr>
      </tbody>
    </table>
    <table className="lp-cs-table lp-cs-cast" aria-hidden="true">
      <thead>
        <tr><th>#</th><th>Cast</th><th>Status</th><th>Pickup</th><th>MU/Hair</th><th>On set</th></tr>
      </thead>
      <tbody>
        {CALL_CAST.map(([id, name, status, pickup, makeup, onSet]) => (
          <tr key={id}>
            <td>{id}</td><td>{name}</td><td><strong>{status}</strong></td><td>{pickup}</td><td>{makeup}</td><td>{onSet}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SidesVisual: React.FC = () => (
  <div
    className="lp-paper lp-sides"
    role="img"
    aria-label="A sides page for scene 6 with Sarah's lines highlighted, one line crossed out and a margin note."
  >
    <div className="lp-sides-page" aria-hidden="true">
      <p className="lp-sp-slug"><span>6</span>INT. AIRLOCK 7 - DAY<span>6</span></p>
      <p className="lp-sp-character">SARAH</p>
      <p className="lp-sp-dialogue"><span className="lp-hl">Vance didn’t authorize a detour.</span></p>
      <p className="lp-sp-character">COLE</p>
      <p className="lp-sp-dialogue lp-struck">I can loop the GPS feed for ninety minutes.</p>
      <p className="lp-sp-character">SARAH</p>
      <p className="lp-sp-dialogue"><span className="lp-hl">If we’re not back in eight hours, they’ll send a search party.</span></p>
      <span className="lp-sides-note">Cut per director — 10/14</span>
    </div>
  </div>
);

const DOOD_ROWS = [
  ['1 Sarah', ['SW', 'W', 'W', 'H', 'WF']],
  ['2 Cole', ['SW', 'W', 'W', 'WF', '']],
  ['3 Vance', ['SW', 'H', 'H', 'WF', '']],
  ['6 Ravi', ['SWF', '', '', '', '']],
] as const;

const DOOD_STYLE = new Map(
  PRODUCTION_WORK_STATUS_PRESENTATIONS.map(status => [status.code as string, status.doodBadge]),
);

export const DoodVisual: React.FC = () => (
  <div
    className="lp-panel lp-dood"
    role="img"
    aria-label="Day out of days grid for four cast members across five shooting days, using start, work, hold and finish codes."
  >
    <table aria-hidden="true">
      <thead>
        <tr>
          <th>Cast</th>
          {[1, 2, 3, 4, 5].map(day => <th key={day}>D{day}</th>)}
        </tr>
      </thead>
      <tbody>
        {DOOD_ROWS.map(([name, days]) => (
          <tr key={name}>
            <td>{name}</td>
            {days.map((code, index) => {
              const badge = code ? DOOD_STYLE.get(code) : undefined;
              return (
                <td key={index}>
                  {badge && (
                    <span className="lp-dood-code" style={{ background: badge.background, color: badge.text, borderColor: badge.border }}>
                      {code}
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const STORYBOARD_FRAMES = [
  { src: '/demo-assets/scifi_airlock.webp', shot: '6A', meta: 'Wide · Eye level · Static · 24mm' },
  { src: '/demo-assets/scifi_rover.webp', shot: '8B', meta: 'Tracking · Low angle · 35mm' },
  { src: '/demo-assets/scifi_control_room.webp', shot: '10A', meta: 'Medium · Dolly in · 50mm' },
] as const;

export const StoryboardVisual: React.FC = () => (
  <figure className="lp-panel lp-storyboard">
    <div className="lp-panel-bar">
      <span>Storyboard</span>
      <span className="lp-panel-meta lp-ai-label">AI-generated frames</span>
    </div>
    <div className="lp-frames">
      {STORYBOARD_FRAMES.map(frame => (
        <div key={frame.shot} className="lp-frame">
          <img src={frame.src} alt="" loading="lazy" decoding="async" width="800" height="800" />
          <span className="lp-frame-shot">{frame.shot}</span>
          <span className="lp-frame-meta">{frame.meta}</span>
        </div>
      ))}
    </div>
    <figcaption className="lp-visually-hidden">
      Three storyboard frames from the demo project, each labelled with its shot number, type, angle, movement and lens.
    </figcaption>
  </figure>
);

const MOOD_PALETTE = ['#1c1a24', '#5e3424', '#b8612f', '#e7a564', '#9cb9c9'] as const;

export const MoodBoardVisual: React.FC = () => (
  <figure className="lp-panel lp-mood">
    <div className="lp-panel-bar">
      <span>Mood Board</span>
      <span className="lp-panel-meta lp-ai-label">AI-generated</span>
    </div>
    <div className="lp-mood-body">
      <img src="/demo-assets/scifi_mars_colony.webp" alt="" loading="lazy" decoding="async" width="800" height="800" />
      <div>
        <span className="lp-sheet-label">Locations &amp; settings</span>
        <div className="lp-palette" aria-hidden="true">
          {MOOD_PALETTE.map(color => <span key={color} style={{ background: color }} />)}
        </div>
        <ul className="lp-chips" aria-label="Look presets">
          {MOOD_LOOK_PRESETS.map(preset => <li key={preset}>{preset}</li>)}
        </ul>
      </div>
    </div>
    <figcaption className="lp-visually-hidden">
      A mood board card with a generated image of the Mars colony, its extracted colour palette, and the look presets.
    </figcaption>
  </figure>
);

export const ReferencesVisual: React.FC = () => (
  <div
    className="lp-panel lp-refs"
    role="img"
    aria-label="Reference search across Pexels, Unsplash, Openverse and Pixabay, and a view-only share link that expires after 30 days."
  >
    <div className="lp-panel-bar" aria-hidden="true">
      <span>References</span>
      <span className="lp-panel-meta lp-stock-label">Stock photos &amp; uploads</span>
    </div>
    <div className="lp-refs-body" aria-hidden="true">
      <div className="lp-search">
        <span className="lp-search-icon" />
        dust storm on the horizon, dusk
      </div>
      <ul className="lp-chips lp-sources">
        {STOCK_SOURCES.map(source => <li key={source}>{source}</li>)}
      </ul>
      <div className="lp-share">
        <div>
          <strong>Share board</strong>
          <span>View-only link · uploads excluded</span>
        </div>
        <div className="lp-segment">
          <span>7 days</span>
          <span className="is-active">30 days</span>
          <span>90 days</span>
        </div>
        <div className="lp-share-link">
          <span>/shared/references/k7Qm…</span>
          <span className="lp-share-revoke">Revoke</span>
        </div>
      </div>
    </div>
  </div>
);
